import {ReactElement, useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router";
import {useDispatch, useSelector} from "react-redux";
import {
    ReferralLinkTextBody,
    ReferralsApi,
    UsersApi,
    OWUserSessionEventType,
    VerificationsApi,
    Token,
    ErrorType,
} from "@devour/client";
import {IStore} from "../redux/defaultStore";
import AuthHeaderLogo from "../components/auth/AuthHeaderLogo";
import {
    addError,
    decrementLoading,
    toggleLoginOpen,
} from "../redux/meta/metaActions";
import getConfig from "../utils/getConfig";
import {AiFillCheckCircle} from "react-icons/ai";
import classNames from "classnames";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import LoginEmail from "@/components/login/LoginEmail";
import LoginRegistration from "@/components/login/LoginRegistration";
import LoginVerificationCode from "@/components/login/LoginVerificationCode";
import {emitLoginEvent, emitRegisterEvent} from "@/hooks/useOnLogin";
import useOnAuthThirdweb from "@/hooks/useOnAuthThirdweb";
import {logOverwolfUserEvent} from "@/utils/logOverwolfUserEvent";
import { useGetGuild } from "@/hooks/useGetGuild";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";
import SocialLoginButtons, {ISocialProvider} from "@/components/login/SocialLoginButtons";
import MagicLoginSuccess from "@/components/login/MagicLoginSuccess";
import SocialProviderProgress from "@/components/login/SocialProviderProgress";
import { getAuthorizedExternalOrigin } from "@/components/auth/AuthLoginRedirect";
import {useGetAcquisitionSignals} from "@/hooks/useGetAcquisitionSignals";

enum LoginState {
    EMAIL,
    REGISTRATION,
    VERIFY_CODE,
    MAGIC_LINK,
    SOCIAL_LOGIN,
}

interface Props {
    isOnDialog?: boolean;
    onInvitedRegistered?: () => void
}

function ThirdwebLogin(props: Props): ReactElement {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const referralCodeQuery = searchParams.get("ref");
    const acquisition = useGetAcquisitionSignals();
    const redirect = searchParams.get("redirect");
    /*
     * URL first, persisted slice second. A fresh tagged arrival wins, and the
     * fallback is what survives the trip to the user's inbox and back.
     */
    const utmSource = searchParams.get("utm_source") || acquisition.utmSource;
    const [loginState, setLoginState] = useState<LoginState>(LoginState.EMAIL);
    const [referralLinkText, setReferralLinkText] = useState<ReferralLinkTextBody | undefined>(undefined);
    const [email, setEmail] = useState<string | null>(null);
    const [profileDataId, setProfileDataId] = useState<string | null>(null);
    // Read friend invite code stored by FriendInvitePage when a non-logged-in user clicks "Join DevourPlay"
    const [friendInviteCode] = useState<string | null>(() => sessionStorage.getItem("friendInviteCode"));
    const [socialProvider, setSocialProvider] = useState<ISocialProvider>();
    const [useMagicLink, setUseMagicLink] = useState<boolean>(false);
    const [hasVerificationCodeError, setHasVerificationCodeError] = useState<boolean>(false);
    const reduxReferralCode = useSelector((store: IStore) => store.affiliateStore.referralCode);
    const reduxReferralExpiry = useSelector((store: IStore) => store.affiliateStore.referralExpiry);
    const reduxReferralValid = reduxReferralCode && Date.now() < reduxReferralExpiry ? reduxReferralCode : "";
    const [referralCode, setReferralCode] = useState<string>(reduxReferralValid ?? referralCodeQuery ?? "");
    const [loginRequestId, setLoginRequestId] = useState<string>("");
    const [isExistingUser, setIsExistingUser] = useState<boolean>(false);
    const {isOnOverwolf} = useOverwolfInterop();
    const {onReauthorize} = useOnAuthThirdweb();
    const navigate = useNavigate();
    const isSubmittingRef = useRef(false);
    const guildInviteId = searchParams.get("guildId");
    const challengeInviteID = searchParams.get("challengeId");
    const oauthError = searchParams.get("error");
    const {data: guildInfo} = useGetGuild(guildInviteId);
    const {data: challengeInfo} = useGetChallenge(challengeInviteID);

    useEffect(() => {
        if (!oauthError) return;
        const messages: Record<string, string> = {
            discord_auth_failed: "Discord sign-in failed. Please try again.",
            google_auth_failed: "Google sign-in failed. Please try again.",
            apple_auth_failed: "Apple sign-in failed. Please try again.",
            auth_failed: "Sign-in failed. Please try again.",
        };
        void addError({type: ErrorType.API, message: messages[oauthError] ?? "Sign-in failed. Please try again."}).then(action => dispatch(action));
    }, [oauthError]);
    const isInvite = !!referralCode;
    const isGuildInvite = isInvite && guildInviteId;
    const isChallengeInvite = isInvite && challengeInviteID;

    useEffect(() => {
        /**
         * Get the current referral link text.
         *
         */
        async function getReferralLinkText() {
            try {
                const formData = await new ReferralsApi(getConfig()).getReferralLinkText({
                    referralCode: reduxReferralCode,
                });

                setReferralLinkText({
                    header: formData.header,
                    description: formData.description,
                    relationshipType: formData.relationshipType,
                });
            } catch (e) {
                dispatch(await addError(e));
            } finally {
                dispatch(decrementLoading());
            }
        }

        // only render if there was a referral code stored
        if (reduxReferralCode) {
            void getReferralLinkText();
        }
    }, [reduxReferralCode]);

    useEffect(() => {
        window.addEventListener("message", windowMessageListener);

        return () => {
            window.removeEventListener("message", windowMessageListener);
        };
    }, []);

    function windowMessageListener(event: MessageEvent): void {
        /*
         * Do we trust the sender of this message?
         * (might be different from what we originally opened, for example).
         */
        if (event.origin !== import.meta.env.VITE_WEB_URL && !getAuthorizedExternalOrigin(event.origin)) return;
        // console.log("windowMessageListener", event.data);

        if (event.data?.type === "devourgo-social-login") {
            // Login was successful and token is applied.
            if (redirect) {
                // Redirect to destination if set
                if (redirect.startsWith("http")) {
                    window.location.href = redirect;
                } else {
                    window.location.href = import.meta.env.VITE_WEB_URL + redirect;
                }
            } else {
                // Just reload current page
                window.location.reload();
            }
        }
        if (event.data?.type === "devourgo-social-registration" && event.data?.profileDataId) {
            setProfileDataId(event.data.profileDataId);
            setEmail(event.data.email);
            setLoginState(LoginState.REGISTRATION);
        }

    }

    async function onSubmitEmail(email: string, useMagicLink: boolean): Promise<void> {
        if (isSubmittingRef.current) {
            return;
        }
        try {
            isSubmittingRef.current = true;
            const res = await new UsersApi(getConfig()).getUsersEmailExists({
                email,
            });
            if (res.disabled) {
                throw new Error("Your account has been disabled.");
            }
            setEmail(email);
            setUseMagicLink(useMagicLink);
            if (useMagicLink) {
                await new VerificationsApi(getConfig()).createPassportMagicSend({
                    requestMagicLinkBody: {
                        destination: email,
                    },
                });
                setLoginState(LoginState.MAGIC_LINK);
            } else if (res._exists) {
                const loginRequest = await new VerificationsApi(getConfig()).createLoginCodeRequest({
                    requestLoginCodeBody: {
                        email: email,
                    },
                });
                setLoginRequestId(loginRequest.loginCodeRequestId);
                setLoginState(LoginState.VERIFY_CODE);
                setIsExistingUser(true);
            } else {
                setIsExistingUser(false);
                setLoginState(LoginState.REGISTRATION);
            }
        } catch (e) {
            dispatch(await addError(e));
        } finally {
            isSubmittingRef.current = false;
        }
    }

    async function onSubmitRegistration(referralCode?: string): Promise<void> {
        if (isSubmittingRef.current) {
            return;
        }
        try {
            isSubmittingRef.current = true;

            // If already verified with social login then just activate profile.
            if (profileDataId) {
                const token = await new VerificationsApi(getConfig()).createPassportActivateProfileId({
                    id: profileDataId,
                    activateProfileBody: {
                        email: email,
                        referralCode: referralCode,
                        // Matches the email branch below: all four, not source alone.
                        ...acquisition,
                        utmSource: utmSource || undefined,
                        friendInviteCode: friendInviteCode || undefined,
                    },
                });
                await onNewLoginComplete(token);
                return;
            }

            // send email verification code
            if (useMagicLink) {
                await new VerificationsApi(getConfig()).createPassportMagicSend({
                    requestMagicLinkBody: {
                        destination: email,
                    },
                });
                setLoginState(LoginState.MAGIC_LINK);
            } else {
                const loginRequest = await new VerificationsApi(getConfig()).createLoginCodeRequest({
                    requestLoginCodeBody: {
                        email: email,
                    },
                });
                setLoginRequestId(loginRequest.loginCodeRequestId);
                setLoginState(LoginState.VERIFY_CODE);
            }

            if (referralCode) {
                setReferralCode(referralCode);
            }
        } catch (e) {
            dispatch(await addError(e));
        } finally {
            isSubmittingRef.current = false;
        }
    }

    async function onSubmitVerificationCode(code: string): Promise<void> {
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;

        try {
            const token = await new VerificationsApi(getConfig()).updateLoginCodeIdVerify({
                id: loginRequestId,
                loginCodeBody: {
                    verificationCode: code,
                    referralCode: referralCode,
                    friendInviteCode: friendInviteCode || undefined,
                    ...acquisition,
                    // URL-first value wins over the stored one, as above.
                    utmSource,
                },
            });
            await onNewLoginComplete(token);
        } catch (e) {
            dispatch(await addError(e));
        } finally {
            isSubmittingRef.current = false;
        }
    }

    async function onRequestNewCode(): Promise<void> {
        setHasVerificationCodeError(false);
        await onSubmitRegistration();
    }

    async function onNewLoginComplete(token: Token): Promise<void> {
        if (!token) {
            return;
        }
        // Clear friend invite code from sessionStorage — it's been processed (or was a no-op)
        sessionStorage.removeItem("friendInviteCode");
        await onReauthorize(token, true);

        dispatch(toggleLoginOpen(false));
        if (isInvite && !isExistingUser && props.onInvitedRegistered) {
            props.onInvitedRegistered();
        }

        if (isExistingUser) {
            emitLoginEvent();
            if (isOnOverwolf) {
                void logOverwolfUserEvent(OWUserSessionEventType.USER_LOGIN);
            }
        } else {
            emitRegisterEvent();
            if (isOnOverwolf) {
                void logOverwolfUserEvent(OWUserSessionEventType.USER_SIGNUP);
            }
        }
        if (!props.isOnDialog) {
            if (redirect && redirect.startsWith("http")) {
                window.location.href = redirect;
            } else if (redirect) {
                navigate(redirect);
            } else {
                navigate("/");
            }
        }
    }


    function onProviderSelect(socialProvider: ISocialProvider): void {
        const currentUrl = new URL(window.location.href);
        const origin = currentUrl.origin;
        const includeState = Boolean(getAuthorizedExternalOrigin(origin));
        const url = new URL(socialProvider.href);

        // Detect if running in Electron
        const isElectron = Boolean((window as any).electronAPI?.openExternal);

        if (isElectron) {
            // Set state to "electron" - backend will redirect to devourplay://
            url.searchParams.set("state", "electron");
            // Open in external browser
            (window as any).electronAPI.openExternal(url.toString());
        } else {
            /*
             * Existing logic for web flows
             * Always include state when utm_source is present so the backend can forward it to the registration URL
             */
            if (includeState || currentUrl.searchParams.has("utm_source")) {
                url.searchParams.set("state", encodeURIComponent(currentUrl.toString()));
            }
            window.open(url.toString(), socialProvider.id, "toolbars=0,width=500,height=700,left=200,top=100,scrollbars=1,resizable=1");
        }
        setSocialProvider(socialProvider);
        setLoginState(LoginState.SOCIAL_LOGIN);
    }

    function getInviteDescription() {
        if (isChallengeInvite) {
            return `Your friend invite you to join them in the ${challengeInfo?.name}. Enter your email address to create an account and then join the challenge to complete!`;
        }
        if (isGuildInvite) {
            return `Your friend invited you to join ${guildInfo?.name} guild in DevourPlay. Please sign up to get started!`;
        }
        if (isInvite) {
            return "You were invited by a friend to join Devour, where gamers earn rewards for playing games! Enter your email below to get started!";
        }
    }


    function renderIntro() {
        if (loginState === LoginState.VERIFY_CODE) {
            return null;
        }

        if (loginState === LoginState.REGISTRATION) {
            return (
                <>
                    <h3 className="login-page_content_title">Create an Account!</h3>
                    <p className="login-page_content_subtitle">
                    Confirm that you are 18 or above, accept terms and then press Create Account to continue.
                    </p>
                </>
            );
        }

        if (isInvite) {
            return (
                <>
                    <h3 className="login-page_content_title">You Are Invited!</h3>
                    <p className="login-page_content_subtitle">
                        {getInviteDescription()}
                    </p>
                </>
            );
        }
        return (
            <>
                {!isOnOverwolf &&
                    <>
                        <h3 className="login-page_content_title">
                            Play. Earn. <span className="dp-auth-accent">Repeat.</span>
                        </h3>
                        <p className="login-page_content_subtitle">
                            Earn XP, Fuel, and drops—just for playing.
                        </p>
                    </>
                }

                {reduxReferralValid && referralLinkText && !isGuildInvite &&
                    <div className="sign-up_content_referral-link-container">
                        <div className="sign-up_content_referral-link-container_header">
                            <AiFillCheckCircle
                                className="sign-up_content_referral-link-container_header_icon"/>
                            <p className="sign-up_content_referral-link-container_text">{referralLinkText.header}</p>
                        </div>
                        {referralLinkText.description && referralLinkText.description !== "" &&
                            <p className="sign-up_content_referral-link-container_text">
                                {referralLinkText.description}
                            </p>
                        }
                    </div>
                }

                <h4 className="login-page_content_login-text">
                    Login or Sign Up
                </h4>
                {isOnOverwolf &&
                    <p className="login-page_content_login-description">
                        DevourPlay delivers more than just food — serving up a new way to eat, play,
                        and earn!
                    </p>
                }
            </>
        );
    }

    function renderForm() {
        switch (loginState) {
            case LoginState.REGISTRATION:
                return (
                    <LoginRegistration
                        initialReferralCode={referralCode}
                        email={email}
                        onSubmit={onSubmitRegistration}
                        disableReferralField={!!(referralCodeQuery && reduxReferralValid)}
                    />
                );
            case LoginState.VERIFY_CODE:
                return (
                    <LoginVerificationCode
                        email={email}
                        hasError={hasVerificationCodeError}
                        onRequestNewCode={onRequestNewCode}
                        onSubmit={onSubmitVerificationCode}
                    />
                );
            case LoginState.MAGIC_LINK:
                return (
                    <MagicLoginSuccess />
                );
            case LoginState.SOCIAL_LOGIN:
                return (
                    <SocialProviderProgress
                        socialProvider={socialProvider}
                        onBack={() => setLoginState(LoginState.EMAIL)}
                    />
                );
            case LoginState.EMAIL:
            default:
                return (
                    <>
                        <SocialLoginButtons
                            onProviderSelect={onProviderSelect}
                        />
                        <LoginEmail
                            initialEmail={email}
                            onSubmit={onSubmitEmail}
                        />
                    </>
                );
        }
    }

    return (
        <div
            className={classNames("login-page", "dp-auth-page", {
                "is-on-dialog": !!props.isOnDialog,
            })}
        >
            {!props.isOnDialog &&
                <div className="login-page_spacer-top">
                    <AuthHeaderLogo/>
                </div>
            }

            <div className="login-page_content">

                {renderIntro()}
                {renderForm()}
            </div>

            <div className="login-page_spacer-bottom"/>
        </div>
    );
}

export default ThirdwebLogin;